import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { IconDefinition, faListOl, faStar } from '@fortawesome/pro-duotone-svg-icons';
import { faFilter as faFilterSolid, faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';
import { Select, Store } from '@ngxs/store';
import { CellClickEvent } from '@progress/kendo-angular-grid';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import _ from 'lodash';
import moment from 'moment';
import { Observable } from 'rxjs';
import { getTopmostTableCellElement } from 'src/app/helper/elementAncestorFunctions';
import { BrickCategory } from 'src/app/samwebDescription/samweb/brickCategoryType';
import { SelectCall } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/call-wait-list-behaviour/call-wait-list-behaviour.actions';
import { CallWaitListBehaviourState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/call-wait-list-behaviour/call-wait-list-behaviour.state';
import { OpenChat } from 'src/app/store/samwin/chats/chats.actions';
import { ChatId } from 'src/app/store/samwin/chats/models/chat-id';
import { AcceptCallFromCallList } from 'src/app/store/samwin/client-phone/client-phone.actions';
import { SamwinCallListState } from 'src/app/store/samwin/samwin-call-list/samwin-call-list.state';
import { SamwinSkillsState } from 'src/app/store/samwin/samwin-skills/samwin-skills.state';
import { Skill } from 'src/app/store/samwin/samwin-skills/skill';
import { ConversationTypeRemoteDto } from 'src/generated/signalR/conversation-type-remote-dto';
import { IciFlags } from 'src/generated/signalR/ici-flags';
import { SamwinCallInfoRemoteDto } from 'src/generated/signalR/samwin-call-info-remote-dto';
import { BasicBrickComponent } from '../../basic-brick-component';
import { SamwebBrick } from '../../samweb-brick.decorator';

@Component({
   selector: 'samweb-skill-wait-list-brick',
   templateUrl: './call-wait-list-brick.component.html',
   styleUrls: ['./call-wait-list-brick.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush,
})
@SamwebBrick({
   brickDefinitionId: 'callWaitList',
   brickTitleTranslationKey: 'brick.brick_definitions.call_wait_list.title',
   brickDescriptionTranslationKey: 'brick.brick_definitions.call_wait_list.description',
   brickIcon: faListOl,
   minColWidth: 10,
   minRowHeight: 10,
   brickCategory: BrickCategory.CALLING,
})
export class CallWaitListBrickComponent extends BasicBrickComponent implements OnInit {
   private static MEMO_TAG = 'MEMO';

   @Select(SamwinCallListState.getCallListFilteredBySkillFilter)
   callList$: Observable<SamwinCallInfoRemoteDto[]>;

   @Select(SamwinCallListState.isCallListFilteredBySkillFilter)
   isCallListFilteredBySkillFilter$: Observable<boolean>;

   @Select(SamwinCallListState.getCallListSkillFilter)
   callListSkillFilter$: Observable<Skill>;

   @ViewChild('cellTooltipDirective')
   public cellTooltipDirective: TooltipDirective;

   public brickInstanceId: string;

   public skillStarsIcon: IconDefinition = faStar;

   public skillStarsIconSolid: IconDefinition = faStarSolid;

   public statusBarFilterIcon: IconDefinition = faFilterSolid;

   constructor(private store: Store) {
      super();
   }

   ngOnInit() {}

   onRowDoubleClick() {
      const selectedCall = this.store.selectSnapshot(CallWaitListBehaviourState.selectedCall)(this.brickInstanceId);

      if (selectedCall?.type === ConversationTypeRemoteDto.Call) {
         this.answerCall(selectedCall);
      } else {
         this.openChat(selectedCall);
      }
   }

   toHHMMSS(waitingSince: moment.Moment) {
      const waitMillis: number = moment().diff(waitingSince, 'milliseconds');
      const waitingDuration = moment.duration(waitMillis);

      return (
         waitingDuration.hours().toString() +
         ':' +
         waitingDuration.minutes().toString().padStart(2, '0') +
         ':' +
         waitingDuration.seconds().toString().padStart(2, '0')
      );
   }

   parkColor(call: SamwinCallInfoRemoteDto) {
      if (call.ici.ici & IciFlags.PARK_PRIVATE) {
         return this.store.selectSnapshot(SamwinSkillsState.getSkillConfigurationFn)(call.queueId)?.agentParkColor;
      }
      if (call.ici.ici & IciFlags.PARK_PUBLIC) {
         return this.store.selectSnapshot(SamwinSkillsState.getSkillConfigurationFn)(call.queueId)?.skillParkColor;
      }
      return null;
   }

   memoValue(call: SamwinCallInfoRemoteDto) {
      // Return memo from info field only if tag is not present (empty string means it's present)
      // Occurs when tag has not been sent to client yet
      const memoTag = call.tags[CallWaitListBrickComponent.MEMO_TAG];
      return _.isNil(memoTag) ? call.info : memoTag;
   }

   onCellClick(event: CellClickEvent) {
      this.store.dispatch(new SelectCall(this.brickInstanceId, event.dataItem, event.rowIndex + 1));
   }

   public showTooltip(e: MouseEvent): void {
      const element = e.target as HTMLElement;
      const tooltipAnchor = getTopmostTableCellElement(element);
      if (tooltipAnchor && (tooltipAnchor.scrollWidth > tooltipAnchor.parentElement.offsetWidth || tooltipAnchor.offsetWidth < tooltipAnchor.scrollWidth)) {
         this.cellTooltipDirective.toggle(tooltipAnchor);
      } else {
         this.cellTooltipDirective.hide();
      }
   }

   public hideTooltip() {
      this.cellTooltipDirective.hide();
   }

   private answerCall(selectedCall: SamwinCallInfoRemoteDto): void {
      const callPosition = this.store.selectSnapshot(CallWaitListBehaviourState.selectedCallPosition)(this.brickInstanceId);
      this.store.dispatch(new AcceptCallFromCallList(selectedCall, callPosition));
   }

   private openChat(selectedChat: SamwinCallInfoRemoteDto): void {
      this.store.dispatch(new OpenChat(new ChatId(selectedChat?.callId, selectedChat?.messageId)));
   }
}
